// src/features/leases/leaseDetailDatesHelpers.js
//
// LEASE-DETAIL-V5-S4: Pure grouping helper for the Dates urgency register.
// No React dependency — safe to import from tests directly.
//
// groupCriticalDates(dates, today) → { actionNeeded, upcoming, history }
//
// Partitioning rules:
//   history      — dueDate is in the past (< today)
//   actionNeeded — future date with an action-required bucket type
//   upcoming     — future date with an informational bucket type
//
// Ordering within each group: ascending by dueDate (soonest first).
// History is descending (most recent first) for de-emphasis readability.
//
// LEASE-DATES-REDESIGN additions:
//   buildTimelineEvents(criticalDates, lease, abatementRows) — for SVG timeline
//   computeNoticeWindow(criticalDates) — for the notice-window gauge
//   fmtYearsOut(days) — "~N yrs" / "~N mo" human-readable countdown

import { parseDateOnly, isSentinelEndDate } from "./leaseTabConfig.js";

// Bucket types that demand a calendar decision or external action.
const ACTION_BUCKETS = new Set([
  "renewal_notice",
  "break_option",
  "break_notice",
  "expiration",
  "allowance_deadline",
  "allowance_warning",
  "allowance_expiry",
  "deposit_burndown",
]);

// Types whose notice-window gauge is meaningful (lease optionality events).
const NOTICE_WINDOW_TYPES = new Set(["renewal_notice", "break_option"]);

// Dates that can represent an actionable lease milestone, plus the narrower
// notice/break dates that can open a current-term notice window.
export const CURRENT_TERM_KEY_DATE_TYPES = new Set([
  "expiration",
  "renewal_notice",
  "notice_deadline",
  "break_option",
  "break_notice",
  "renewal_option",
]);
export const CURRENT_TERM_WINDOW_TYPES = new Set([
  "renewal_notice",
  "notice_deadline",
  "break_option",
  "break_notice",
]);

const CURRENT_TERM_MARKER = "current term commencement";
const DOCUMENT_REVIEW_PROVENANCE = new Set([
  "document_review",
  "document_reviewed",
  "lease_document_review",
  "lease_document_reviewed",
  "reviewed_lease_document",
]);

function hasExplicitVerification(record) {
  if (!record || typeof record !== "object") return false;
  if (
    record.verified === true
    || record.isVerified === true
    || record.verifiedSource === true
    || record.isVerifiedSource === true
    || record.dateVerified === true
    || record.isDateVerified === true
  ) return true;

  const verificationStatus = String(
    record.verificationStatus ?? record.verification_status ?? "",
  ).trim().toLowerCase();
  if (verificationStatus === "verified") return true;

  return [
    record.provenance,
    record.source,
    record.sourceType,
    record.source_type,
    record.reviewProvenance,
    record.review_provenance,
  ].some((value) => DOCUMENT_REVIEW_PROVENANCE.has(
    String(value ?? "").trim().toLowerCase().replaceAll("-", "_").replaceAll(" ", "_"),
  ));
}

/**
 * A populated or ordinarily sourced date is not sufficient evidence for a
 * notice/break decision. Only an explicit verified signal or document-review
 * provenance unlocks it. For lease fields, verification can be supplied via
 * their fieldSources entry or a field-specific verified flag.
 */
export function isVerifiedLeaseDecisionDate(record, fieldName = "dueDate") {
  if (fieldName === "dueDate" && hasExplicitVerification(record)) return true;

  const snakeFieldName = String(fieldName).replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  const fieldSources = record?.fieldSources ?? record?.field_sources;
  const source = fieldSources && (
    fieldSources[fieldName] ?? fieldSources[snakeFieldName]
  );
  if (typeof source === "string") {
    return DOCUMENT_REVIEW_PROVENANCE.has(
      source.trim().toLowerCase().replaceAll("-", "_").replaceAll(" ", "_"),
    );
  }
  if (hasExplicitVerification(source)) return true;

  if (fieldName !== "dueDate") {
    const camelProvenance = record?.[`${fieldName}Provenance`];
    const snakeProvenance = record?.[`${snakeFieldName}_provenance`];
    const fieldProvenance = camelProvenance ?? snakeProvenance;
    const normalizedProvenance = String(fieldProvenance ?? "")
      .trim()
      .toLowerCase()
      .replaceAll("-", "_")
      .replaceAll(" ", "_");
    const camelFlag = `${fieldName}Verified`;
    const isCamelFlag = `${fieldName}IsVerified`;
    const snakeFlag = `${snakeFieldName}_verified`;
    const snakeIsFlag = `${snakeFieldName}_is_verified`;
    const camelStatus = `${fieldName}VerificationStatus`;
    const snakeStatus = `${snakeFieldName}_verification_status`;
    return DOCUMENT_REVIEW_PROVENANCE.has(normalizedProvenance)
      || record?.[camelFlag] === true
      || record?.[isCamelFlag] === true
      || record?.[snakeFlag] === true
      || record?.[snakeIsFlag] === true
      || String(record?.[camelStatus] ?? record?.[snakeStatus] ?? "").trim().toLowerCase() === "verified";
  }
  return false;
}

function dateOnly(value) {
  if (!value) return null;
  const parsed = parseDateOnly(value);
  return parsed && parsed.getFullYear() <= 2100 ? parsed : null;
}

function localDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateKey(value) {
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? localDateKey(value) : null;
  const match = String(value ?? "").match(/^(\d{4}-\d{2}-\d{2})/);
  return match && dateOnly(value) ? match[1] : null;
}

function todayDateKey(today) {
  if (!(today instanceof Date)) return dateKey(today);
  // Timeline callers provide UTC midnight; Dates and list callers use local
  // midnight. Preserve the caller's intended calendar day in either case.
  if (today.getUTCHours() === 0 && today.getUTCMinutes() === 0 && today.getUTCSeconds() === 0) {
    return today.toISOString().slice(0, 10);
  }
  return localDateKey(today);
}

function noticePeriodBoundary(cd) {
  const label = String(cd.categoryRaw ?? cd.category_raw ?? cd.label ?? "").trim();
  const match = label.match(/^(.*\bnotice period)\s+(starts|ends)$/i);
  if (!match) return null;
  return {
    kind: match[2].toLowerCase() === "starts" ? "start" : "end",
    key: match[1].toLowerCase().replace(/\s+/g, " "),
  };
}

function sameNoticePeriod(left, right) {
  const leftBoundary = noticePeriodBoundary(left);
  const rightBoundary = noticePeriodBoundary(right);
  if (!leftBoundary || !rightBoundary || leftBoundary.key !== rightBoundary.key) return false;
  const leftAreas = String(left.areas ?? "").trim().toLowerCase();
  const rightAreas = String(right.areas ?? "").trim().toLowerCase();
  return !leftAreas || !rightAreas || leftAreas === rightAreas;
}

/**
 * Resolve the currently relevant notice/break window, including imported
 * notice-period start/end rows. One-date notices retain the historical rule:
 * after their date they remain open until expiry. Explicitly bounded periods
 * close after their end date instead.
 */
export function selectCurrentTermNoticeWindow(
  criticalDates,
  lease,
  today = new Date(),
  types = CURRENT_TERM_WINDOW_TYPES,
) {
  const selection = selectCurrentTermKeyDates(criticalDates, lease, today);
  const currentWindowDates = selection.windowDates.filter((cd) => types.has(cd.type));
  const allDateBoundaries = (criticalDates || [])
    .filter((cd) => types.has(cd.type) && cd.dueDate)
    .map((cd) => ({ ...cd, _date: dateOnly(cd.dueDate), _dateKey: dateKey(cd.dueDate) }))
    .filter((cd) => cd._date && cd._dateKey);
  const todayKey = selection.todayKey;
  const expiryValue = lease?.endDate && !isSentinelEndDate(lease.endDate)
    ? lease.endDate
    : lease?.soonestPossibleExpiration;
  const expiryDate = dateOnly(expiryValue);
  const expiryKey = dateKey(expiryValue);
  const expiryIsFuture = !expiryKey || expiryKey > todayKey;
  const candidates = [];
  const pairedEnds = new Set();

  for (const start of currentWindowDates) {
    const boundary = noticePeriodBoundary(start);
    if (boundary?.kind !== "start") continue;
    const end = currentWindowDates
      .filter((item) => noticePeriodBoundary(item)?.kind === "end"
        && !pairedEnds.has(item)
        && sameNoticePeriod(start, item)
        && item._dateKey >= start._dateKey)
      .sort((a, b) => a._dateKey.localeCompare(b._dateKey))[0] || null;
    if (end) pairedEnds.add(end);
    candidates.push({
      source: start,
      end,
      dateKey: start._dateKey,
      date: start._date,
      endKey: end?._dateKey || null,
      endDate: end?._date || null,
      explicitEnd: !!end,
    });
  }

  for (const end of currentWindowDates) {
    if (noticePeriodBoundary(end)?.kind !== "end" || pairedEnds.has(end)) continue;
    const start = allDateBoundaries
      .filter((item) => noticePeriodBoundary(item)?.kind === "start"
        && sameNoticePeriod(item, end)
        && item._dateKey <= end._dateKey)
      .sort((a, b) => b._dateKey.localeCompare(a._dateKey))[0] || null;
    candidates.push({
      source: end,
      end,
      dateKey: start?._dateKey || end._dateKey,
      date: start?._date || end._date,
      endKey: end._dateKey,
      endDate: end._date,
      explicitEnd: true,
    });
  }

  for (const item of currentWindowDates) {
    if (noticePeriodBoundary(item)) continue;
    candidates.push({
      source: item,
      end: null,
      dateKey: item._dateKey,
      date: item._date,
      endKey: null,
      endDate: null,
      explicitEnd: false,
    });
  }

  const evaluated = candidates
    .filter((candidate) => candidate.endKey == null || candidate.endKey >= todayKey)
    .map((candidate) => {
      const startsInFuture = candidate.dateKey > todayKey;
      const state = !expiryIsFuture ? "expired"
        : startsInFuture ? "notice-future"
          : "notice-open";
      return { ...candidate, state };
    })
    .filter((candidate) => candidate.state !== "expired" || !candidate.explicitEnd);
  const future = evaluated
    .filter((candidate) => candidate.state === "notice-future")
    .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
  const open = evaluated
    .filter((candidate) => candidate.state === "notice-open")
    .sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  const expiredSingle = evaluated
    .filter((candidate) => candidate.state === "expired" && !candidate.explicitEnd)
    .sort((a, b) => b.dateKey.localeCompare(a.dateKey));
  const selected = future[0] || open[0] || expiredSingle[0] || null;

  if (!selected) {
    return {
      state: "no-notice",
      noticeDt: null,
      expiryDt: expiryDate,
      noticeType: null,
      noticeDateValue: null,
      periodEndDt: null,
      periodEndDateValue: null,
    };
  }

  return {
    state: selected.state,
    noticeDt: selected.date,
    expiryDt: expiryDate,
    noticeType: selected.source.type,
    noticeDateValue: selected.source.dueDate,
    periodEndDt: selected.endDate,
    periodEndDateValue: selected.end?.dueDate || null,
  };
}

/**
 * Resolve the current-term lower bound.
 *
 * The latest explicit "Current Term Commencement" critical date wins; if
 * there is no explicit marker, use the lease start date as the best available
 * anchor. Without either, dates remain eligible because no lower bound is
 * known.
 */
export function getCurrentTermStartDate(criticalDates, lease) {
  const markers = (criticalDates || [])
    .filter((cd) => {
      const label = cd.categoryRaw ?? cd.category_raw ?? cd.label ?? "";
      return String(label).trim().toLowerCase() === CURRENT_TERM_MARKER;
    })
    .map((cd) => dateOnly(cd.dueDate))
    .filter(Boolean)
    .sort((a, b) => b - a);

  return markers[0] || dateOnly(lease?.startDate);
}

/**
 * Apply one current-term rule to every lease-date surface.
 *
 * Key dates before the current-term start are historical, not active signals.
 * Among eligible dates, the next-key-date candidate is the nearest date on or
 * after today; elapsed dates never outrank an upcoming actionable date.
 */
export function selectCurrentTermKeyDates(criticalDates, lease, today = new Date()) {
  const currentTermStart = getCurrentTermStartDate(criticalDates, lease);
  const currentTermStartKey = dateKey(currentTermStart);
  const todayKey = todayDateKey(today);
  const eligibleDates = (criticalDates || [])
    .filter((cd) => CURRENT_TERM_KEY_DATE_TYPES.has(cd.type) && cd.dueDate)
    .map((cd) => ({ ...cd, _date: dateOnly(cd.dueDate), _dateKey: dateKey(cd.dueDate) }))
    .filter((cd) => cd._date && cd._dateKey && (!currentTermStartKey || cd._dateKey >= currentTermStartKey))
    .sort((a, b) => a._dateKey.localeCompare(b._dateKey));
  const expiryValue = lease?.endDate && !isSentinelEndDate(lease.endDate)
    ? lease.endDate
    : lease?.soonestPossibleExpiration;
  const expiryKey = dateKey(expiryValue);
  const outsideExpiryDates = expiryKey
    ? eligibleDates.filter((cd) => cd._dateKey > expiryKey)
    : [];
  const currentTermDates = expiryKey
    ? eligibleDates.filter((cd) => cd._dateKey <= expiryKey)
    : eligibleDates;
  const windowDates = expiryKey
    ? currentTermDates.filter((cd) => CURRENT_TERM_WINDOW_TYPES.has(cd.type))
    : [];
  const nextUpcomingKeyDate = currentTermDates.find((cd) => cd._dateKey >= todayKey) || null;
  const nextUpcomingWindowDate = windowDates.find((cd) => cd._dateKey >= todayKey) || null;
  const mostRecentElapsedWindowDate = [...windowDates]
    .reverse()
    .find((cd) => cd._dateKey < todayKey) || null;

  return {
    currentTermStart,
    expiryKey,
    todayKey,
    eligibleDates: currentTermDates,
    outsideExpiryDates,
    windowDates,
    nextUpcomingKeyDate,
    nextUpcomingWindowDate,
    mostRecentElapsedWindowDate,
  };
}

// Human-readable label map for buildTimelineEvents labels.
const TIMELINE_LABEL = {
  renewal_notice:    "Renewal Notice",
  break_option:      "Break Option",
  break_notice:      "Break Notice",
  expiration:        "Expiry",
  escalation:        "Escalation",
  deposit_burndown:  "Deposit",
  allowance_deadline:"TI Deadline",
  allowance_warning: "TI Warning",
  allowance_expiry:  "TI Expiry",
  cam_audit:         "CAM Audit",
  other:             "Other",
};

/**
 * Compute days-from-today for a dueDate string (YYYY-MM-DD or ISO).
 * Returns null when unparseable.
 *
 * @param {string} dateStr
 * @param {Date}   today   — caller-supplied so tests can freeze time
 * @returns {number|null}
 */
function daysFromToday(dateStr, today) {
  if (!dateStr) return null;
  const dt = parseDateOnly(dateStr);
  if (!dt) return null;
  const midnight = new Date(today);
  midnight.setHours(0, 0, 0, 0);
  return Math.ceil((dt.getTime() - midnight.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Convert a day-count to a human-readable "~N yrs" / "~N mo" string.
 * Operates on absolute days (sign ignored); returns null for null input.
 *
 * @param {number|null} days
 * @returns {string|null}
 */
export function fmtYearsOut(days) {
  if (days == null || !Number.isFinite(days)) return null;
  const abs = Math.abs(days);
  if (abs >= 365) {
    const yrs = Math.round(abs / 365 * 10) / 10;
    const whole = Math.round(yrs);
    return `~${whole} yr${whole !== 1 ? "s" : ""}`;
  }
  const mos = Math.round(abs / 30);
  if (mos < 1) return "< 1 mo";
  return `~${mos} mo`;
}

/**
 * Partition critical-date rows into the three register groups.
 *
 * @param {Array<object>} dates  — raw rows from listCriticalDates()
 * @param {Date}          today  — caller-supplied for testability (default: new Date())
 * @returns {{ actionNeeded: Array, upcoming: Array, history: Array }}
 */
export function groupCriticalDates(dates, today = new Date(), lease = null) {
  const actionNeeded = [];
  const upcoming     = [];
  const history      = [];
  const currentTermStartKey = dateKey(getCurrentTermStartDate(dates, lease));
  const expiryValue = lease?.endDate && !isSentinelEndDate(lease.endDate)
    ? lease.endDate
    : lease?.soonestPossibleExpiration;
  const expiryKey = dateKey(expiryValue);

  for (const cd of dates) {
    const date = dateOnly(cd.dueDate);
    const dueDateKey = dateKey(cd.dueDate);
    const isCurrentTermKeyDate = CURRENT_TERM_KEY_DATE_TYPES.has(cd.type) && date && dueDateKey;
    const isPriorTermKeyDate = isCurrentTermKeyDate
      && currentTermStartKey
      && dueDateKey < currentTermStartKey;
    const isAfterCurrentExpiry = isCurrentTermKeyDate
      && expiryKey
      && dueDateKey > expiryKey;
    const days = daysFromToday(cd.dueDate, today);
    if (isPriorTermKeyDate) {
      history.push({ ...cd, daysRemaining: days });
    } else if (days == null) {
      // Undateable → treat as informational upcoming
      upcoming.push({ ...cd, daysRemaining: null });
      continue;
    } else if (days < 0) {
      history.push({ ...cd, daysRemaining: days });
    } else if (isAfterCurrentExpiry) {
      // Keep out-of-term future rows visible, but don't present them as active
      // decisions for this lease term.
      upcoming.push({ ...cd, daysRemaining: days });
    } else if (ACTION_BUCKETS.has(cd.type)) {
      actionNeeded.push({ ...cd, daysRemaining: days });
    } else {
      upcoming.push({ ...cd, daysRemaining: days });
    }
  }

  // Ascending (soonest first) for action + upcoming; descending (most recent first) for history.
  actionNeeded.sort((a, b) => (a.daysRemaining ?? Infinity) - (b.daysRemaining ?? Infinity));
  upcoming.sort((a, b) => (a.daysRemaining ?? Infinity) - (b.daysRemaining ?? Infinity));
  history.sort((a, b) => (b.daysRemaining ?? -Infinity) - (a.daysRemaining ?? -Infinity));

  return { actionNeeded, upcoming, history };
}

/**
 * Build a sorted array of typed timeline events for the SVG timeline.
 * Includes synthetic commencement and expiry events derived from the lease,
 * plus all critical dates. Abatement bands are returned separately.
 *
 * @param {Array<object>}  criticalDates  — raw rows from listCriticalDates()
 * @param {object|null}    lease          — selectedLease (needs startDate, endDate)
 * @param {Array<object>}  abatementRows  — allowances filtered to type === 'abatement'
 * @returns {{ events: Array, abatementBands: Array }}
 *
 * Each event: { type, date, label, id? }
 * Each abatementBand: { start, end, label }
 */
export function buildTimelineEvents(criticalDates, lease, abatementRows = []) {
  const events = [];

  if (lease?.startDate) {
    events.push({
      type:  "commencement",
      date:  lease.startDate,
      label: "Start",
    });
  }

  if (lease?.endDate && !isSentinelEndDate(lease.endDate)) {
    const endDt = parseDateOnly(lease.endDate);
    // Exclude far-future sentinel-style dates (year > 2100) that represent
    // open-ended leases stored with a placeholder year rather than the
    // canonical 9999-12-31 sentinel.
    if (endDt && endDt.getFullYear() <= 2100) {
      events.push({
        type:  "expiry_anchor",
        date:  lease.endDate,
        label: "Expiry",
      });
    }
  }

  // Group escalations into a single synthetic cluster; others pushed individually.
  const escalationRows = [];
  for (const cd of (criticalDates || [])) {
    if (!cd.dueDate) continue;
    if (cd.type === "escalation") {
      escalationRows.push(cd);
    } else {
      const genericLabel = TIMELINE_LABEL[cd.type] || "Other";
      events.push({
        type:        cd.type,
        date:        cd.dueDate,
        label:       genericLabel,
        description: cd.description || null,
        id:          cd.id,
      });
    }
  }

  if (escalationRows.length > 0) {
    // Sort to find the earliest escalation date for the cluster anchor.
    escalationRows.sort((a, b) => {
      const da = parseDateOnly(a.dueDate);
      const db = parseDateOnly(b.dueDate);
      if (!da || !db) return 0;
      return da.getTime() - db.getTime();
    });
    events.push({
      type:  "escalation_group",
      date:  escalationRows[0].dueDate,
      label: "Escalations (annual)",
      count: escalationRows.length,
    });
  }

  // Sort chronologically.
  events.sort((a, b) => {
    const da = parseDateOnly(a.date);
    const db = parseDateOnly(b.date);
    if (!da || !db) return 0;
    return da.getTime() - db.getTime();
  });

  // Abatement bands: start → expiry date of abatement-type allowances.
  const abatementBands = (abatementRows || [])
    .filter((r) => r.startDate && r.expiryDate)
    .map((r) => ({
      start: r.startDate,
      end:   r.expiryDate,
      label: r.label || "Abatement",
    }));

  return { events, abatementBands };
}

/**
 * Compute the notice-window gauge data for the nearest renewal_notice or
 * break_option critical date.
 *
 * The notice window is typically 180 days (6 months) before the deadline.
 * Returns null when no applicable critical date exists.
 *
 * @param {Array<object>} criticalDates
 * @returns {{ opens: Date, deadline: Date, type: string, rawDate: string } | null}
 */
export function computeNoticeWindow(criticalDates) {
  const candidates = (criticalDates || [])
    .filter((cd) => NOTICE_WINDOW_TYPES.has(cd.type) && cd.dueDate)
    .map((cd) => ({ ...cd, _dt: parseDateOnly(cd.dueDate) }))
    .filter((cd) => cd._dt !== null)
    .sort((a, b) => a._dt.getTime() - b._dt.getTime());

  if (candidates.length === 0) return null;

  const nearest  = candidates[0];
  const deadline = nearest._dt;
  const opens    = new Date(deadline);
  opens.setDate(opens.getDate() - 180);

  return {
    opens,
    deadline,
    type:    nearest.type,
    rawDate: nearest.dueDate,
  };
}
